import React, { useCallback, useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger, SheetClose } from './ui/sheet';
import { Button } from './ui/button';
import {
  Handshake, Trash2, CheckCircle2, XCircle, CalendarDays, User, MapPin,
  Gamepad2, Copy, ArrowRight, Inbox, Send, Loader2, RotateCcw, Headphones, MicOff, X, MessageCircle,
} from 'lucide-react';
import { RARITIES, VARIANTS } from '../mock';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useTrades } from '../hooks/useTrades';
import { api } from '../lib/api';
import TradeChatDialog from './TradeChatDialog';

const STATUS = {
  pending:   { label: 'Pending',   color: '#ffaa1c', bg: '#fff3d5' },
  accepted:  { label: 'Accepted',  color: '#10b981', bg: '#d6f5e8' },
  declined:  { label: 'Declined',  color: '#ef4444', bg: '#ffe1e1' },
  completed: { label: 'Completed', color: '#3a1064', bg: '#ece0ff' },
  cancelled: { label: 'Cancelled', color: '#ef4444', bg: '#ffe1e1' },
};

function formatMeet(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function TradesSheet({
  trades: tradesProp,
  onUpdate: onUpdateProp,
  onRemove: onRemoveProp,
  onClearAll: onClearAllProp,
  trigger,
}) {
  const { isAuthed, user } = useAuth();
  // Self-load local drafts when used without explicit props (e.g. in Header)
  const localHook = useTrades();
  const trades     = tradesProp ?? localHook.trades;
  const onUpdate   = onUpdateProp ?? localHook.updateTrade;
  const onRemove   = onRemoveProp ?? localHook.removeTrade;
  const onClearAll = onClearAllProp ?? localHook.clearAll;
  const [open, setOpen] = useState(false);
  const [serverTrades, setServerTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatTrade, setChatTrade] = useState(null); // trade currently being chatted about

  const sent     = serverTrades.filter((t) => t.sender_id === user?.id);
  const received = serverTrades.filter((t) => t.recipient_id === user?.id);
  const incomingPending = received.filter((t) => t.status === 'pending').length;
  const drafts = trades; // legacy localStorage entries
  const pendingDrafts = drafts.filter((t) => t.status === 'pending').length;

  const fetchServer = useCallback(async () => {
    if (!isAuthed) return;
    setLoading(true);
    try {
      const list = await api.listMyTrades();
      setServerTrades(list);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  useEffect(() => {
    if (open && isAuthed) fetchServer();
  }, [open, isAuthed, fetchServer]);

  const respond = async (id, mode) => {
    try {
      const fn = mode === 'accept' ? api.acceptTradeReq : api.declineTradeReq;
      await fn(id, { response_note: '' });
      toast({ title: `Trade ${mode}ed` });
      fetchServer();
    } catch (e) {
      toast({ title: `Could not ${mode}`, description: e.message });
    }
  };

  const cancel = async (id) => {
    try {
      await api.cancelTradeReq(id);
      toast({ title: 'Trade cancelled' });
      fetchServer();
    } catch (e) {
      toast({ title: 'Could not cancel', description: e.message });
    }
  };

  const removeServer = async (id) => {
    if (!window.confirm('Delete this trade from your list? This cannot be undone.')) return;
    try {
      await api.deleteTradeReq(id);
      toast({ title: 'Trade deleted' });
      // optimistic — drop it locally so the sheet feels instant
      setServerTrades((prev) => prev.filter((t) => t.id !== id));
      fetchServer();
    } catch (e) {
      toast({ title: 'Could not delete', description: e.message });
    }
  };

  const copyOne = (t) => {
    const offerLine = t.spriteName || (t.offer_name || '(nothing — borrow)');
    const wantLine = t.wantName || t.want_name || '(anything)';
    const recipient = t.recipient || t.recipient_username || '';
    const text = `🤝 Fortnite Sprite Trade\nI'll give: ${offerLine}\nI want: ${wantLine}\nTo: ${recipient}\nPlatform: ${t.platform || ''} · Mode: ${t.mode || ''}\nWhen: ${formatMeet(t.meetAt || t.meet_at)}` +
      ((t.location) ? `\nWhere: ${t.location}` : '') +
      ((t.note) ? `\nNote: ${t.note}` : '');
    navigator.clipboard?.writeText(text);
    toast({ title: 'Trade copied' });
  };

  const totalBadge = incomingPending + pendingDrafts;

  return (
    <>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button
            data-testid="open-trades-sheet-btn"
            className="h-10 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black shadow-[0_3px_0_#3a1064] gap-2"
          >
            <Handshake className="h-4 w-4" /> Trades
            {totalBadge > 0 && (
              <span
                data-testid="trades-pending-badge"
                className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-black bg-yellow-300 text-[#3a1064] rounded-full border-2 border-[#3a1064]"
              >
                {totalBadge}
              </span>
            )}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent
        data-testid="trades-sheet"
        className="w-full sm:max-w-[480px] bg-[#fff7d6] border-l-[3px] border-[#3a1064] overflow-y-auto p-0"
      >
        <div className="sticky top-0 z-10 bg-[#ffd900] border-b-[3px] border-[#3a1064] p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <SheetHeader className="text-left space-y-1 flex-1 min-w-0">
              <SheetTitle className="text-[#3a1064] font-black text-xl sm:text-2xl flex items-center gap-2">
                <Handshake className="h-5 w-5 sm:h-6 sm:w-6 shrink-0" /> Your Trades
              </SheetTitle>
              <SheetDescription className="text-[#3a1064]/80 font-bold text-sm">
                {isAuthed
                  ? `${received.length} received · ${sent.length} sent`
                  : 'Sign in to see trades sent to you.'}
              </SheetDescription>
            </SheetHeader>
            <SheetClose asChild>
              <button
                data-testid="close-trades-sheet-btn"
                aria-label="Close trades"
                className="h-10 w-10 rounded-xl bg-white border-[3px] border-[#3a1064] text-[#3a1064] shadow-[0_3px_0_#3a1064] hover:bg-yellow-50 flex items-center justify-center shrink-0 active:translate-y-0.5 active:shadow-[0_1px_0_#3a1064] transition"
              >
                <X className="h-5 w-5" strokeWidth={3} />
              </button>
            </SheetClose>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {loading && isAuthed && serverTrades.length === 0 && (
            <div className="text-center py-6"><Loader2 className="h-6 w-6 mx-auto animate-spin text-[#9d3dff]" /></div>
          )}

          {/* Received (incoming) */}
          {isAuthed && (
            <section data-testid="trades-received-section">
              <div className="flex items-center gap-2 mb-2">
                <Inbox className="h-4 w-4 text-[#ff2bd6]" />
                <span className="font-black uppercase tracking-wider text-[#3a1064] text-xs">
                  Received ({received.length})
                </span>
              </div>
              {received.length === 0 ? (
                <EmptyHint text="No trade requests received yet." />
              ) : (
                <div className="space-y-3">
                  {received.map((t) => (
                    <ServerTradeCard
                      key={t.id} t={t} role="recipient"
                      onAccept={() => respond(t.id, 'accept')}
                      onDecline={() => respond(t.id, 'decline')}
                      onDelete={() => removeServer(t.id)}
                      onCopy={() => copyOne(t)}
                      onChat={() => setChatTrade(t)}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Sent (outgoing) */}
          {isAuthed && (
            <section data-testid="trades-sent-section">
              <div className="flex items-center gap-2 mb-2">
                <Send className="h-4 w-4 text-[#9d3dff]" />
                <span className="font-black uppercase tracking-wider text-[#3a1064] text-xs">
                  Sent ({sent.length})
                </span>
              </div>
              {sent.length === 0 ? (
                <EmptyHint text="You haven't sent any trade requests yet. Click any sprite's Trade button." />
              ) : (
                <div className="space-y-3">
                  {sent.map((t) => (
                    <ServerTradeCard
                      key={t.id} t={t} role="sender"
                      onCancel={() => cancel(t.id)}
                      onDelete={() => removeServer(t.id)}
                      onCopy={() => copyOne(t)}
                      onChat={() => setChatTrade(t)}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Legacy local drafts */}
          {drafts.length > 0 && (
            <section data-testid="trades-drafts-section">
              <div className="flex items-center justify-between mb-2">
                <span className="font-black uppercase tracking-wider text-[#3a1064] text-xs">
                  Local drafts ({drafts.length})
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-lg border-2 border-[#3a1064] bg-white text-[#3a1064] font-black"
                  onClick={() => {
                    if (window.confirm('Clear all local drafts?')) {
                      onClearAll();
                      toast({ title: 'Drafts cleared' });
                    }
                  }}
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Clear
                </Button>
              </div>
              <div className="space-y-3">
                {drafts.map((t) => (
                  <LegacyDraftCard
                    key={t.id} t={t}
                    onUpdate={onUpdate}
                    onRemove={onRemove}
                    onCopy={() => copyOne(t)}
                  />
                ))}
              </div>
            </section>
          )}

          {!isAuthed && drafts.length === 0 && (
            <div className="text-center py-12">
              <Handshake className="h-12 w-12 mx-auto text-[#ff2bd6]" />
              <div className="mt-3 font-black text-[#3a1064] text-lg">No trades yet</div>
              <div className="text-sm font-bold text-[#3a1064]/70 mt-1">
                Sign in and click <span className="text-[#ff2bd6]">Trade</span> on a sprite to send a request.
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
    {chatTrade && (
      <TradeChatDialog
        trade={chatTrade}
        userId={user?.id}
        onClose={() => setChatTrade(null)}
      />
    )}
    </>
  );
}

function EmptyHint({ text }) {
  return (
    <div className="bg-white/60 border-2 border-dashed border-[#3a1064]/30 rounded-xl py-3 px-3 text-center text-xs font-bold text-[#3a1064]/70">
      {text}
    </div>
  );
}

function ServerTradeCard({ t, role, onAccept, onDecline, onCancel, onDelete, onCopy, onChat }) {
  const rarity = RARITIES[t.want_rarity] || RARITIES.rare;
  const variant = VARIANTS[t.want_variant] || VARIANTS.base;
  const offerRarity = t.offer_rarity ? (RARITIES[t.offer_rarity] || RARITIES.rare) : null;
  const offerVariant = t.offer_variant ? (VARIANTS[t.offer_variant] || VARIANTS.base) : null;
  const status = STATUS[t.status] || STATUS.pending;
  const isBorrow = t.trade_type === 'borrow';

  return (
    <div className="bg-white rounded-2xl border-[3px] border-[#3a1064] shadow-[0_4px_0_#3a1064] overflow-hidden">
      <div className="p-3 border-b-2 border-[#3a1064]/15" style={{ background: `linear-gradient(135deg, ${variant.color}22, ${rarity.color}33)` }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 flex items-center gap-1">
            {isBorrow ? <><RotateCcw className="h-3 w-3" /> Borrow</> : '1-for-1 Swap'}
            {t.has_mic ? <Headphones className="h-3 w-3 text-emerald-600 ml-1" title="Has mic" /> : <MicOff className="h-3 w-3 text-rose-500 ml-1" title="No mic" />}
          </span>
          <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full border-2" style={{ background: status.bg, color: status.color, borderColor: status.color }}>
            {status.label}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5">
          {/* Their (sender's) offer */}
          {t.offer_id ? (
            <div className="bg-white/80 rounded-lg p-2 border-2 border-[#3a1064] flex items-center gap-2 min-w-0">
              <img src={t.offer_img} alt="" className="h-9 w-9 object-contain shrink-0" />
              <div className="min-w-0">
                <div className="text-[8px] font-black uppercase tracking-wider text-[#3a1064]/70">{role === 'recipient' ? 'You get' : 'You give'}</div>
                <div className="text-[11px] font-black text-[#3a1064] truncate leading-tight">{t.offer_name}</div>
                <div className="flex items-center gap-0.5 mt-0.5">
                  <span className="text-[7px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: offerRarity.color }}>{offerRarity.label}</span>
                  <span className="text-[7px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: offerVariant.color }}>{offerVariant.label}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 rounded-lg p-2 border-2 border-dashed border-[#3a1064]/50 text-center">
              <div className="text-[9px] font-black uppercase tracking-wider text-[#3a1064]/70">{isBorrow ? 'Borrow & return' : 'Nothing in return'}</div>
            </div>
          )}
          <div className="h-7 w-7 rounded-full bg-[#ffd900] border-2 border-[#3a1064] flex items-center justify-center shadow-[0_2px_0_#3a1064]">
            <ArrowRight className="h-4 w-4 text-[#3a1064]" strokeWidth={3} />
          </div>
          {/* Their (sender's) wanted sprite */}
          <div className="bg-white/80 rounded-lg p-2 border-2 border-[#3a1064] flex items-center gap-2 min-w-0">
            <img src={t.want_img} alt="" className="h-9 w-9 object-contain shrink-0" />
            <div className="min-w-0">
              <div className="text-[8px] font-black uppercase tracking-wider text-[#3a1064]/70">{role === 'recipient' ? 'You give' : 'You get'}</div>
              <div className="text-[11px] font-black text-[#3a1064] truncate leading-tight">{t.want_name}</div>
              <div className="flex items-center gap-0.5 mt-0.5">
                <span className="text-[7px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: rarity.color }}>{rarity.label}</span>
                <span className="text-[7px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: variant.color }}>{variant.label}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 space-y-1.5 text-sm text-[#3a1064] font-bold">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-[#ff2bd6]" />
          {role === 'recipient' ? <>From <span className="font-black">@{t.sender_username}</span> {t.sender_name && <span className="text-[#3a1064]/60">({t.sender_name})</span>}</>
            : <>To <span className="font-black">@{t.recipient_username}</span> {t.recipient_name && <span className="text-[#3a1064]/60">({t.recipient_name})</span>}</>}
        </div>
        {t.epic_id && <div className="flex items-center gap-2"><User className="h-4 w-4 text-[#3a1064]/60" /> Epic: <span className="font-black">{t.epic_id}</span></div>}
        {t.meet_at && <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#9d3dff]" /> {formatMeet(t.meet_at)}</div>}
        {(t.platform || t.mode) && <div className="flex items-center gap-2"><Gamepad2 className="h-4 w-4 text-[#00b8ff]" /> {[t.platform, t.mode].filter(Boolean).join(' · ')}</div>}
        {t.location && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-emerald-500" /> {t.location}</div>}
        {t.note && <div className="text-xs text-[#3a1064]/80 bg-yellow-50 border-l-4 border-yellow-400 px-2 py-1.5 rounded">{t.note}</div>}
        {t.response_note && <div className="text-xs text-[#3a1064] bg-emerald-50 border-l-4 border-emerald-400 px-2 py-1.5 rounded"><span className="font-black">Reply:</span> {t.response_note}</div>}
      </div>

      <div className="p-3 pt-0 flex flex-wrap items-center gap-2">
        {role === 'recipient' && t.status === 'pending' && (
          <>
            <Button
              data-testid={`accept-trade-${t.id}`}
              size="sm"
              className="h-8 rounded-lg border-2 border-[#3a1064] bg-emerald-500 hover:bg-emerald-600 text-white font-black shadow-[0_2px_0_#3a1064]"
              onClick={onAccept}
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> Accept
            </Button>
            <Button
              data-testid={`decline-trade-${t.id}`}
              size="sm"
              variant="outline"
              className="h-8 rounded-lg border-2 border-[#3a1064] bg-white text-rose-700 font-black"
              onClick={onDecline}
            >
              <XCircle className="h-4 w-4 mr-1" /> Decline
            </Button>
          </>
        )}
        {role === 'sender' && t.status === 'pending' && (
          <Button
            data-testid={`cancel-trade-${t.id}`}
            size="sm"
            variant="outline"
            className="h-8 rounded-lg border-2 border-[#3a1064] text-[#3a1064] bg-white font-black"
            onClick={onCancel}
          >
            <XCircle className="h-4 w-4 mr-1" /> Cancel
          </Button>
        )}
        {onDelete && ['declined', 'cancelled', 'accepted'].includes(t.status) && (
          <Button
            data-testid={`delete-trade-${t.id}`}
            size="sm"
            variant="outline"
            className="h-8 rounded-lg border-2 border-rose-500 bg-white text-rose-700 hover:bg-rose-50 font-black"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        )}
        {onChat && (
          <Button
            data-testid={`chat-trade-${t.id}`}
            size="sm"
            variant="outline"
            className="h-8 rounded-lg border-2 border-[#ff2bd6] bg-white text-[#ff2bd6] hover:bg-pink-50 font-black shadow-[0_2px_0_#3a1064]"
            onClick={onChat}
          >
            <MessageCircle className="h-4 w-4 mr-1" strokeWidth={3} /> Chat
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-8 rounded-lg border-2 border-[#3a1064] text-[#3a1064] bg-white font-black ml-auto"
          onClick={onCopy}
        >
          <Copy className="h-4 w-4 mr-1" /> Copy
        </Button>
      </div>
    </div>
  );
}

function LegacyDraftCard({ t, onUpdate, onRemove, onCopy }) {
  const rarity = RARITIES[t.spriteRarity] || RARITIES.rare;
  const variant = VARIANTS[t.spriteVariant] || VARIANTS.base;
  const status = STATUS[t.status] || STATUS.pending;
  return (
    <div className="bg-white rounded-2xl border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] p-3 opacity-90">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70">Local draft</span>
        <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full border-2" style={{ background: status.bg, color: status.color, borderColor: status.color }}>
          {status.label}
        </span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        {t.spriteImg && <img src={t.spriteImg} alt="" className="h-10 w-10 object-contain bg-yellow-50 rounded border-2 border-[#3a1064] p-0.5" />}
        <div className="min-w-0 flex-1">
          <div className="font-black text-[#3a1064] text-sm truncate">{t.spriteName || 'Borrow'} → {t.wantName || '?'}</div>
          <div className="text-xs font-bold text-[#3a1064]/70">To {t.recipient}</div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="h-7 rounded-lg border-2 border-[#3a1064] text-[#3a1064] bg-white font-black" onClick={() => onUpdate(t.id, { status: 'cancelled' })}>
          <XCircle className="h-3 w-3 mr-1" /> Mark cancelled
        </Button>
        <Button size="sm" variant="outline" className="h-7 rounded-lg border-2 border-[#3a1064] text-[#3a1064] bg-white font-black" onClick={onCopy}>
          <Copy className="h-3 w-3 mr-1" /> Copy
        </Button>
        <Button size="sm" variant="outline" className="h-7 rounded-lg border-2 border-rose-300 text-rose-600 bg-white font-black ml-auto" onClick={() => onRemove(t.id)}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
